import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, message, Modal, Space } from 'antd';
import moment from 'moment';
import { getReadableStatementNumber, OrderType } from 'types';
import {
	SAVE_BDO_STATEMENT_COMMAND_TYPE,
	SaveBdoStatementCommand,
	SIGN_BDO_STATEMENT_COMMAND_TYPE,
	SignBdoStatementCommand,
} from 'types/commands';
import API from '../../../../../API';
import { useBdoStatement } from 'reducers';
import { SignaturePad, SignaturePadHandle } from './signature-pad';
import { BdoStatementDefaults } from './bdo-statement-defaults';
import './bdo-statement-form.scss';

interface Props {
	orderType: OrderType.loose | OrderType.classic;
	orderId: number;
	canSign: boolean;
	readOnly: boolean;
	defaults?: BdoStatementDefaults;
	onChanged: () => void;
}

export const BdoStatementForm: React.FC<Props> = ({ orderType, orderId, canSign, readOnly, defaults, onChanged }) => {
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	// Signing phase: the driver has saved the details and is now collecting the customer's signature.
	const [signing, setSigning] = useState(false);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [statement, refresh] = useBdoStatement(orderType, orderId ?? 0);
	const [form] = Form.useForm();
	const signaturePadRef = useRef<SignaturePadHandle>(null);

	const isSigned = !!statement?.signedAt;
	const defaultSignedDate = moment();

	// Full-screen signing pad sized to the viewport (handover devices: tablet/phone).
	const padWidth = Math.min(window.innerWidth - 64, 1200);
	const padHeight = Math.max(window.innerHeight - 320, 220);

	useEffect(() => {
		if (statement && visible) {
			form.setFieldsValue({
				wasteCode: statement.wasteCode,
				wasteName: statement.wasteName,
				pickupAddress: statement.pickupAddress,
				companyName: statement.companyName,
				registeredAddress: statement.registeredAddress,
				nipPesel: statement.nipPesel,
				signedPlace: statement.signedPlace,
				signedDate: statement.signedDate ? moment(statement.signedDate) : undefined,
			});
		}
	}, [statement, visible, form]);

	// Phase 1 — the driver saves the statement details. No signature here.
	const handleSave = async () => {
		// validateFields rejects on invalid input; antd shows the inline field errors,
		// so swallow the rejection here rather than letting it surface as unhandled.
		const values = await form.validateFields().catch(() => null);
		if (!values) {
			return;
		}

		setLoading(true);
		try {
			// Empty fields fall back to the default shown as their placeholder.
			const command: SaveBdoStatementCommand = {
				orderId,
				orderType,
				wasteCode: values.wasteCode || defaults?.wasteCode,
				wasteName: values.wasteName || defaults?.wasteName,
				pickupAddress: values.pickupAddress || defaults?.pickupAddress,
				companyName: values.companyName || defaults?.companyName,
				registeredAddress: values.registeredAddress || defaults?.registeredAddress,
				nipPesel: values.nipPesel || defaults?.nipPesel,
				signedPlace: values.signedPlace || defaults?.signedPlace,
				signedDate: (values.signedDate ?? defaultSignedDate).format('YYYY-MM-DD'),
			};
			await API.sendCommand(SAVE_BDO_STATEMENT_COMMAND_TYPE, command);
			await refresh();
			onChanged();
			message.success('Oświadczenie zapisane');
		} catch (e) {
			message.error(String(e));
		} finally {
			setLoading(false);
		}
	};

	// Enter the signing phase — full-screen pad. The driver shows the PDF separately ("Podgląd PDF").
	const handleStartSigning = () => {
		setSigning(true);
	};

	// Phase 2 — the customer signs. Dedicated SignBdoStatement command carrying only the signature.
	const handleSign = async () => {
		if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
			message.error('Proszę złożyć podpis');
			return;
		}
		// Trim to the drawn strokes + a 20px margin so the stored image isn't the whole pad.
		const signature = signaturePadRef.current.toTrimmedDataURL(20, 'image/png');

		setLoading(true);
		try {
			const command: SignBdoStatementCommand = {
				orderId,
				orderType,
				signature,
			};
			// Sign-once: deterministic key collapses double-submits.
			await API.sendCommand(SIGN_BDO_STATEMENT_COMMAND_TYPE, command, `SignBdoStatement:${orderType}:${orderId}`);
			await refresh();
			onChanged();
			message.success('Oświadczenie podpisane');
			setSigning(false);
		} catch (e) {
			message.error(String(e));
		} finally {
			setLoading(false);
		}
	};

	const handlePreviewPdf = async () => {
		// Open the popup synchronously (within the click gesture) so it isn't blocked,
		// then point it at the blob URL once the PDF is fetched.
		const popup = window.open('', 'bdo-statement-pdf', 'popup,width=900,height=1000');
		if (!popup) {
			message.error('Nie udało się otworzyć okna podglądu — sprawdź blokadę wyskakujących okien.');
			return;
		}

		setPdfLoading(true);
		try {
			const blob = await API.orders2.order(orderType, orderId).statementPdf();
			const url = URL.createObjectURL(blob);
			popup.location.href = url;
			// Release the blob once the popup has had time to load it.
			window.setTimeout(() => URL.revokeObjectURL(url), 60000);
		} catch (e) {
			popup.close();
			message.error(String(e));
		} finally {
			setPdfLoading(false);
		}
	};

	const handleClose = () => {
		setVisible(false);
		setSigning(false);
	};

	return (
		<>
			<Button type="primary" size="large" onClick={() => setVisible(true)}>
				{statement?.number ? `Oświadczenie (${getReadableStatementNumber(statement)})` : 'Dodaj oświadczenie'}
			</Button>

			<Modal
				title="Oświadczenie o wytwarzaniu odpadów"
				className="bdo-statement-modal"
				open={visible}
				onCancel={handleClose}
				footer={null}
				width={signing ? '100vw' : 650}
				{...(signing
					? {
							style: { top: 0, maxWidth: '100vw', paddingBottom: 0 },
							bodyStyle: { height: 'calc(100vh - 110px)', overflow: 'auto' },
					  }
					: {})}
			>
				<div style={{ display: signing ? 'none' : 'block' }}>
					{/* Every field is required on the statement, but one with a default may be left empty. */}
					<Form form={form} layout="vertical" disabled={isSigned || readOnly}>
						<Form.Item name="wasteCode" label="Kod odpadu" required rules={[{ required: !defaults?.wasteCode }]}>
							<Input placeholder={defaults?.wasteCode} />
						</Form.Item>
						<Form.Item name="wasteName" label="Rodzaj odpadu" required rules={[{ required: !defaults?.wasteName }]}>
							<Input placeholder={defaults?.wasteName} />
						</Form.Item>
						<Form.Item
							name="pickupAddress"
							label="Adres odbioru"
							required
							rules={[{ required: !defaults?.pickupAddress }]}
						>
							<Input placeholder={defaults?.pickupAddress} />
						</Form.Item>
						<Form.Item name="companyName" label="Nazwa firmy" required rules={[{ required: !defaults?.companyName }]}>
							<Input placeholder={defaults?.companyName} />
						</Form.Item>
						<Form.Item
							name="registeredAddress"
							label="Adres siedziby"
							required
							rules={[{ required: !defaults?.registeredAddress }]}
						>
							<Input placeholder={defaults?.registeredAddress} />
						</Form.Item>
						<Form.Item name="nipPesel" label="NIP / PESEL" required rules={[{ required: !defaults?.nipPesel }]}>
							<Input placeholder={defaults?.nipPesel} />
						</Form.Item>
						<Form.Item
							name="signedPlace"
							label="Miejsce podpisania"
							required
							rules={[{ required: !defaults?.signedPlace }]}
						>
							<Input placeholder={defaults?.signedPlace} />
						</Form.Item>
						<Form.Item name="signedDate" label="Data podpisania" required>
							<DatePicker
								format="DD.MM.YYYY"
								placeholder={defaultSignedDate.format('DD.MM.YYYY')}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Form>

					{isSigned && statement?.signature && (
						<div style={{ marginBottom: 16 }}>
							<div style={{ marginBottom: 4 }}>Podpis</div>
							<img src={statement.signature} alt="Podpis" style={{ border: '1px solid #d9d9d9', maxWidth: '100%' }} />
						</div>
					)}
				</div>

				{signing && !isSigned && (
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<div style={{ marginBottom: 4 }}>Podpis klienta</div>
						<SignaturePad ref={signaturePadRef} width={padWidth} height={padHeight} />
						<div>
							<Button size="small" onClick={() => signaturePadRef.current?.clear()} style={{ marginTop: 4 }}>
								Wyczyść podpis
							</Button>
						</div>
					</div>
				)}

				<Space wrap style={{ marginTop: 16 }}>
					{!isSigned && !signing && !readOnly && (
						<Button type="primary" onClick={handleSave} loading={loading}>
							Zapisz oświadczenie
						</Button>
					)}
					{!isSigned && !signing && !readOnly && canSign && statement && (
						<Button onClick={handleStartSigning}>Pokaż klientowi i podpisz</Button>
					)}
					{signing && (
						<Button type="primary" onClick={handleSign} loading={loading}>
							Podpisz
						</Button>
					)}
					{signing && <Button onClick={() => setSigning(false)}>Wróć do edycji</Button>}
					{statement && (
						<Button onClick={handlePreviewPdf} loading={pdfLoading}>
							Podgląd PDF
						</Button>
					)}
					<Button onClick={handleClose}>Zamknij</Button>
				</Space>
			</Modal>
		</>
	);
};
